/**
 * What the site shows for each ORM and database. The warning, fix and SQL
 * strings are the engine's real output for the example projects in
 * /examples; test/showcase.test.ts fails if they drift apart.
 */

export type OrmKey = "prisma" | "drizzle" | "typeorm";
export type DbKey = "postgresql" | "mysql";

export const ORMS: { key: OrmKey; label: string }[] = [
  { key: "prisma", label: "Prisma" },
  { key: "drizzle", label: "Drizzle" },
  { key: "typeorm", label: "TypeORM" },
];

export const DATABASES: { key: DbKey; label: string }[] = [
  { key: "postgresql", label: "PostgreSQL" },
  { key: "mysql", label: "MySQL" },
];

/** Syntax-highlighted query code. `<u>` marks the span Plinth underlines. */
export const CODE: Record<OrmKey, { file: string; firstLine: number; lines: string[] }> = {
  prisma: {
    file: "orders.ts",
    firstLine: 7,
    lines: [
      `<k>export async function</k> <f>recentOrders</f>(status: <t>OrderStatus</t>) {`,
      `  <k>return</k> prisma.<u>order.findMany</u>({`,
      `    where: { status },`,
      `    orderBy: { createdAt: <s>"desc"</s> },`,
      `    take: <n>50</n>,`,
      `  });`,
      `}`,
    ],
  },
  drizzle: {
    file: "orders.ts",
    firstLine: 7,
    lines: [
      `<k>export async function</k> <f>recentOrders</f>(status: <t>OrderStatus</t>) {`,
      `  <k>return</k> db`,
      `    .select()`,
      `    .<u>from(orders)</u>`,
      `    .where(<f>eq</f>(orders.status, status))`,
      `    .orderBy(<f>desc</f>(orders.createdAt))`,
      `    .limit(<n>50</n>);`,
      `}`,
    ],
  },
  typeorm: {
    file: "orders.service.ts",
    firstLine: 15,
    lines: [
      `<f>recentOrders</f>(status: <t>string</t>) {`,
      `  <k>return this</k>.orders.<u>find</u>({`,
      `    where: { status },`,
      `    order: { createdAt: <s>"DESC"</s> },`,
      `    take: <n>50</n>,`,
      `  });`,
      `}`,
    ],
  },
};

/**
 * The table-size entry each example's plinth.config.json declares for the
 * table the hero query reads. test/showcase.test.ts checks it against the file.
 */
export const CONFIG: Record<OrmKey, { key: string; entry: Record<string, unknown> }> = {
  prisma: { key: "Order", entry: { rows: 5000000, distinct: { status: 4 } } },
  drizzle: { key: "orders", entry: { rows: 5000000, distinct: { status: 4 } } },
  typeorm: { key: "orders", entry: { rows: 5000000, distinct: { status: 4 } } },
};

/** That entry as compact, highlighted JSON lines. */
export function configLines(orm: OrmKey): string[] {
  const { key, entry } = CONFIG[orm];
  const value = (v: unknown): string =>
    typeof v === "number"
      ? `<n>${v}</n>`
      : typeof v === "string" || typeof v === "boolean"
        ? `<s>${JSON.stringify(v)}</s>`
        : `{ ${Object.entries(v as Record<string, unknown>)
            .map(([k, inner]) => `<a>"${k}"</a>: ${value(inner)}`)
            .join(", ")} }`;
  // Compact: the first field on the key's line, each other field on its own.
  const fields = Object.entries(entry).map(([k, v]) => `<a>"${k}"</a>: ${value(v)}`);
  const pad = " ".repeat(key.length + 6);
  return fields.map(
    (field, i) =>
      `${i === 0 ? `<a>"${key}"</a>: { ` : pad}${field}${i < fields.length - 1 ? "," : " }"}`,
  );
}

export interface ShowcaseFix {
  message: string;
  /** The read/write mix the editor shows after the message (workloadLabel). */
  ratio: string;
  detail: string;
  title: string;
  /** Where the schema change goes, and the change itself as highlighted lines. */
  schemaFile: string;
  schema: string[];
  /** The migration file Plinth writes; Drizzle leaves that to drizzle-kit. */
  migrationFile: string | undefined;
  sql: string;
  followUp: string;
}

const MESSAGE: Record<OrmKey, string> = {
  prisma:
    "order.findMany filters on status and sorts by createdAt, but no index on Order supports it.",
  drizzle:
    "select().from(orders) filters on status and sorts by createdAt, but no index on orders supports it.",
  typeorm:
    "orders.find filters on status and sorts by createdAt, but no index on Order supports it.",
};

const TITLE: Record<OrmKey, string> = {
  prisma: "Add @@index([status, createdAt]) to Order and create its migration",
  drizzle: 'Add index("orders_status_created_at_idx").on(table.status, table.createdAt) to orders',
  typeorm:
    'Add @Index("orders_status_created_at_idx", ["status", "createdAt"]) to Order and create its migration',
};

const SQL: Record<DbKey, string> = {
  postgresql:
    'CREATE INDEX CONCURRENTLY IF NOT EXISTS "orders_status_created_at_idx" ON "orders" ("status", "created_at");',
  mysql:
    "CREATE INDEX `orders_status_created_at_idx` ON `orders` (`status`, `created_at`) ALGORITHM=INPLACE LOCK=NONE;",
};

// What happens after the fix, including when the online build does not apply.
const FOLLOW_UP: Record<OrmKey, Record<DbKey, string>> = {
  prisma: {
    postgresql: "Apply it with prisma migrate deploy.",
    mysql: "Apply it with prisma migrate deploy.",
  },
  drizzle: {
    postgresql:
      "Run drizzle-kit generate to create the migration. On a busy table, run the statement above by hand instead: drizzle-kit migrations run in a transaction, which CONCURRENTLY cannot.",
    mysql: "Run drizzle-kit generate to create the migration, or run the statement above by hand.",
  },
  typeorm: {
    postgresql:
      'Apply it with typeorm migration:run. Built CONCURRENTLY when migrationsTransactionMode is "each" or "none"; under the default "all" the migration uses a plain CREATE INDEX.',
    mysql: "Apply it with typeorm migration:run.",
  },
};

const LABEL: Record<DbKey, string> = { postgresql: "PostgreSQL", mysql: "MySQL" };

export function showcase(orm: OrmKey, db: DbKey): ShowcaseFix {
  const table = db === "postgresql" ? "pgTable" : "mysqlTable";
  const schema: Record<OrmKey, { file: string; lines: string[] }> = {
    prisma: {
      file: "prisma/schema.prisma",
      lines: [
        `<k>model</k> <t>Order</t> {`,
        `  <c>…</c>`,
        `  <a>@@map</a>(<s>"orders"</s>)`,
        `<ins>  <a>@@index</a>([status, createdAt])</ins>`,
        `}`,
      ],
    },
    drizzle: {
      file: "src/schema.ts",
      lines: [
        `<k>export const</k> orders = <f>${table}</f>(<s>"orders"</s>, {`,
        `  <c>…</c>`,
        `  createdAt: <f>timestamp</f>(<s>"created_at"</s>).<f>notNull</f>(),`,
        `}<ins>, (table) =&gt; [<f>index</f>(<s>"orders_status_created_at_idx"</s>).<f>on</f>(table.status, table.createdAt)]</ins>);`,
      ],
    },
    typeorm: {
      file: "src/entities.ts",
      lines: [
        `<ins><a>@Index</a>(<s>"orders_status_created_at_idx"</s>, [<s>"status"</s>, <s>"createdAt"</s>])</ins>`,
        `<a>@Entity</a>(<s>"orders"</s>)`,
        `<k>export class</k> <t>Order</t> {`,
        `  <c>…</c>`,
      ],
    },
  };

  return {
    message: MESSAGE[orm],
    // Each example project reads Order in four places and never writes it.
    ratio: "4 reads : 0 writes in code",
    detail: `Without a supporting index ${LABEL[db]} reads every row of "orders" and sorts the result on each call. Fast with staging data, slow at production scale. ${orm === "drizzle" ? "orders" : "Order"} is declared at ~5M rows in plinth.config.json. Write cost: every insert and delete on "orders", and every update to status and createdAt, also updates this index.`,
    title: TITLE[orm],
    schemaFile: schema[orm].file,
    schema: schema[orm].lines,
    migrationFile:
      orm === "prisma"
        ? "prisma/migrations/20261010143000_add_orders_status_created_at_index/migration.sql"
        : orm === "typeorm"
          ? "src/migrations/1791642600000-AddOrdersStatusCreatedAtIndex.ts"
          : undefined,
    sql: SQL[db],
    followUp: FOLLOW_UP[orm][db],
  };
}

/** Turn the tiny highlight markup above into HTML spans. */
export function highlight(line: string): string {
  return line
    .replace(/<(k|f|t|s|n|a|c)>/g, '<span class="tok-$1">')
    .replace(/<\/(k|f|t|s|n|a|c)>/g, "</span>")
    .replace(/<u>/g, '<span class="squiggle">')
    .replace(/<\/u>/g, "</span>")
    .replace(/<ins>/g, '<span class="added">')
    .replace(/<\/ins>/g, "</span>");
}

/** Escape text for HTML (SQL contains quotes and backticks). */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
